import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Search, ExternalLink, Calendar, ChevronDown, ChevronLeft, ChevronRight, Loader2 } from "lucide-react";
import { format, parseISO } from "date-fns";
import {
  getCompanyNews, setCompanyNewsCache, clearCompanyNewsCacheEntry,
  updateSearchedCompanyStatus, addSearchedCompany, getSearchedCompany,
  clearSearchedCompanyReport, derivePermalink, getRelativeTime,
  getConfirmedWebsite, setConfirmedWebsite, getStoredReportId, storeReportId,
} from "@/lib/cache";
import { API } from "@/App";

// ─── Type Colors ─────────────────────────────────────────────────────────────────
const TYPE_COLORS = {
  "Product launches": { bg: "#2A5B4615", text: "#2A5B46", border: "#2A5B46" },
  "M&A":              { bg: "#E9F1EC", text: "#2A5B46", border: "#97C8A6" },
  "Investments":      { bg: "#3A3A3A15", text: "#3A3A3A", border: "#3A3A3A" },
  "Compliance":       { bg: "#9AA0A615", text: "#6B7280", border: "#9AA0A6" },
  "Hiring":           { bg: "#9AA0A615", text: "#6B7280", border: "#9AA0A6" },
  "Partnerships":     { bg: "#9AA0A615", text: "#6B7280", border: "#9AA0A6" },
  "Competition":      { bg: "#9AA0A615", text: "#6B7280", border: "#9AA0A6" },
  "Regulatory":       { bg: "#9AA0A615", text: "#6B7280", border: "#9AA0A6" },
  "Research":         { bg: "#9AA0A615", text: "#6B7280", border: "#9AA0A6" },
};
const DEFAULT_TYPE_COLOR = { bg: "#F4F5F6", text: "#6B7280", border: "#DCDFE2" };

// ─── Article Card ─────────────────────────────────────────────────────────────────
function ArticleCard({ article }) {
  const [expanded, setExpanded] = useState(false);
  const c = TYPE_COLORS[article.type] || DEFAULT_TYPE_COLOR;

  const formattedDate = article.published_date
    ? (() => { try { return format(parseISO(article.published_date), "MMM d, yyyy"); } catch { return article.published_date; } })()
    : null;

  return (
    <div className="py-4 transition-colors" style={{ borderBottom: "1px solid #F0F2F4" }}>
      <div className="flex items-start gap-3">
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-[#0A0A0A] leading-snug mb-1.5" style={{ fontSize: "14px" }}>
            {article.url ? (
              <a href={article.url} target="_blank" rel="noopener noreferrer" className="hover:underline" data-testid={`article-link-${(article.title || "").slice(0, 20)}`}>
                {article.title}
              </a>
            ) : article.title}
          </h3>
          <div className="flex items-center gap-2 flex-wrap mb-2">
            {article.publisher && <span className="text-[11px] text-[#9AA0A6]">{article.publisher}</span>}
            {formattedDate && (
              <span className="text-[11px] text-[#9AA0A6] flex items-center gap-1">
                <Calendar size={10} />{formattedDate}
              </span>
            )}
            {article.type && (
              <span className="text-[10px] font-semibold px-2 py-0.5 rounded" style={{ background: c.bg, color: c.text, border: `1px solid ${c.border}` }}>
                {article.type}
              </span>
            )}
          </div>
          {article.ai_summary && (
            <>
              <p className={`text-[13px] text-[#4B5563] leading-relaxed ${expanded ? "" : "line-clamp-3"}`}>
                {article.ai_summary}
              </p>
              {article.ai_summary.length > 180 && (
                <button
                  onClick={() => setExpanded(!expanded)}
                  className="text-[11px] text-[#6B7280] hover:text-[#0A0A0A] flex items-center gap-1 mt-1 transition-colors"
                >
                  <ChevronDown size={11} className={expanded ? "rotate-180" : ""} />
                  {expanded ? "Show less" : "Read more"}
                </button>
              )}
            </>
          )}
        </div>
        {article.url && (
          <a href={article.url} target="_blank" rel="noopener noreferrer" className="flex-shrink-0 text-[#9AA0A6] hover:text-[#0A0A0A] transition-colors mt-1">
            <ExternalLink size={14} />
          </a>
        )}
      </div>
    </div>
  );
}

// ─── In-Progress Status Panel (States 2 & 3) ──────────────────────────────────────
function CompanyStatusPanel({ permalink, companyName, reportId: initialReportId, initialStatus, onFetchSuccess }) {
  const navigate = useNavigate();

  // ── Resolve report_id from 3 storage places ──
  const resolveReportId = (init) => {
    if (init && !isNaN(parseInt(init, 10))) return parseInt(init, 10);
    try {
      const v = localStorage.getItem(`wokelo_report_id_${permalink}`);
      const n = parseInt(v, 10);
      if (n && !isNaN(n)) return n;
    } catch {}
    try {
      const v = localStorage.getItem(`wokelo_company_news_${permalink}`);
      if (v) { const e = JSON.parse(v); const n = parseInt(e?.reportId || e?.report_id, 10); if (n && !isNaN(n)) return n; }
    } catch {}
    return init || null;
  };

  const [uiState,       setUiState]       = useState(initialStatus);
  const [reportId,      setReportId]       = useState(() => resolveReportId(initialReportId));
  const [statusLabel,   setStatusLabel]   = useState(() => initialStatus === "in_progress" ? "Processing" : "Completed");
  const [fetching,      setFetching]      = useState(false);
  const [resetConfirm,  setResetConfirm]  = useState(false);
  const [resetting,     setResetting]     = useState(false);
  const [zeroArticles,  setZeroArticles]  = useState(false);
  const [manualIdInput, setManualIdInput] = useState("");
  const [manualFetching, setManualFetching] = useState(false);

  const pollRef  = useRef(null);
  const mountRef = useRef(true);

  useEffect(() => {
    mountRef.current = true;
    return () => { mountRef.current = false; if (pollRef.current) clearInterval(pollRef.current); };
  }, []);

  const stopPolling = useCallback(() => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
  }, []);

  const checkStatus = useCallback(async (rId) => {
    if (!mountRef.current || !rId) return;
    try {
      const res = await fetch(`${API}/company-news/status/${rId}`);
      if (!res.ok || !mountRef.current) return;
      const data = await res.json();
      const s = (data.status || "processing").toLowerCase();
      if (s === "completed" || s === "exported") {
        stopPolling();
        if (mountRef.current) { setUiState("completed_not_fetched"); setStatusLabel("Completed"); updateSearchedCompanyStatus(permalink, "completed_not_fetched", rId); }
      } else if (s === "no data found") {
        stopPolling();
        if (mountRef.current) { setUiState("no_data"); updateSearchedCompanyStatus(permalink, "no_data_found", rId); }
      } else {
        if (mountRef.current) {
          const label = s === "queued" ? "Queued" : s === "delayed" ? "Delayed" : "Processing";
          setStatusLabel(label);
          updateSearchedCompanyStatus(permalink, s, rId);
        }
      }
    } catch { /* silent */ }
  }, [permalink, stopPolling]);

  useEffect(() => {
    if (uiState === "in_progress" && reportId) {
      checkStatus(reportId);
      pollRef.current = setInterval(() => { if (mountRef.current) checkStatus(reportId); }, 10000);
      return () => stopPolling();
    }
    stopPolling();
  }, [uiState, reportId, checkStatus, stopPolling]);

  // ── Core fetch helper ──
  const doFetch = useCallback(async (rId) => {
    const id = parseInt(rId, 10);
    if (!id || isNaN(id)) throw new Error("Invalid report_id");
    console.log(`[CompanyStatusPanel] Fetching news for ${permalink}, report_id=${id}`);
    console.log('[Fetch payload being sent]', { report_id: id, page: 1, page_size: 100 });
    const res = await fetch(`${API}/company-news/fetch`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ report_id: id, page: 1, page_size: 100 }),
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const fd = await res.json();
    console.log('[Fetch API response articles count]', fd?.data?.length ?? 0);
    return fd;
  }, [permalink]);

  const handleFetchNews = async () => {
    const rId = resolveReportId(reportId);
    if (!rId || fetching) return;
    setFetching(true);
    setZeroArticles(false);
    try {
      const fd = await doFetch(rId);
      if (!mountRef.current) { setFetching(false); return; }
      const articles = fd?.data || [];
      if (articles.length === 0) {
        console.warn(`[CompanyStatusPanel] 0 articles returned for ${permalink} - report may still be processing`);
        setFetching(false);
        setZeroArticles(true);
        return;
      }
      setCompanyNewsCache(permalink, articles, rId, companyName);
      updateSearchedCompanyStatus(permalink, "completed", rId);
      if (mountRef.current) {
        setFetching(false);
        const fresh = getCompanyNews(permalink);
        if (onFetchSuccess) {
          onFetchSuccess(permalink, fresh);
        } else {
          navigate(`/company-news?company=${encodeURIComponent(permalink)}`);
        }
      }
    } catch (err) {
      console.error(`[CompanyStatusPanel] Fetch error:`, err);
      if (mountRef.current) { setFetching(false); setUiState("error"); }
    }
  };

  const handleManualFetch = async () => {
    const rId = parseInt(manualIdInput.trim(), 10);
    if (!rId || isNaN(rId) || manualFetching) return;
    setManualFetching(true);
    setZeroArticles(false);
    try {
      const fd = await doFetch(rId);
      if (!mountRef.current) { setManualFetching(false); return; }
      const articles = fd?.data || [];
      if (articles.length === 0) { setManualFetching(false); setZeroArticles(true); return; }
      // Save new rId to all storage places
      try { localStorage.setItem(`wokelo_report_id_${permalink}`, rId.toString()); } catch {}
      setReportId(rId);
      setCompanyNewsCache(permalink, articles, rId, companyName);
      updateSearchedCompanyStatus(permalink, "completed", rId);
      if (mountRef.current) {
        setManualFetching(false);
        const fresh = getCompanyNews(permalink);
        if (onFetchSuccess) {
          onFetchSuccess(permalink, fresh);
        } else {
          navigate(`/company-news?company=${encodeURIComponent(permalink)}`);
        }
      }
    } catch (err) {
      console.error(`[ManualFetch] error:`, err);
      if (mountRef.current) { setManualFetching(false); }
    }
  };

  const handleGoToMatrix = () => navigate("/portfolio-matrix");

  // ── Reset & Regenerate ──
  const handleReset = async () => {
    setResetting(true);
    clearCompanyNewsCacheEntry(permalink);
    try { localStorage.removeItem(`wokelo_report_id_${permalink}`); } catch {}
    addSearchedCompany(companyName, permalink, permalink, null, "starting");
    const cw = getConfirmedWebsite(permalink) || companyName;
    const payload = cw.includes(".") ? { website: cw } : { permalink: cw };
    try {
      const res = await fetch(`${API}/company-news/start`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error("Start failed");
      const data = await res.json();
      const rId = parseInt(data.report_id, 10);
      updateSearchedCompanyStatus(permalink, "queued", rId);
      try { localStorage.setItem(`wokelo_report_id_${permalink}`, rId.toString()); } catch {}
      setResetConfirm(false);
      navigate("/portfolio-matrix");
    } catch {
      setResetting(false);
      setResetConfirm(false);
    }
  };

  if (uiState === "no_data") {
    return (
      <div className="bg-white rounded-md p-8 text-center" style={{ boxShadow: "0 1px 4px rgba(0,0,0,0.08)" }}>
        <p className="text-[13px] text-[#9AA0A6] italic">No data found for this company.</p>
      </div>
    );
  }

  const displayReportId = resolveReportId(reportId);

  return (
    <div className="bg-white rounded-md p-6" style={{ boxShadow: "0 1px 4px rgba(0,0,0,0.08)" }}>
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          <h2 className="font-bold text-[#0A0A0A] mb-1" style={{ fontFamily: "'Playfair Display', serif", fontSize: "20px" }}>
            {companyName}
          </h2>
          {displayReportId && (
            <p style={{ fontSize: "11px", color: "#9AA0A6", marginBottom: "4px" }}>
              Report ID: <span style={{ color: "#3A3A3A", fontWeight: 600, fontVariantNumeric: "tabular-nums" }}>{displayReportId}</span>
            </p>
          )}
          {uiState === "in_progress" && (
            <div className="flex items-center gap-2 mt-1">
              <div className="w-2 h-2 rounded-full animate-pulse" style={{ background: "#F59E0B" }} />
              <span className="text-[13px] text-[#6B7280]">{statusLabel}... Report generation is underway.</span>
            </div>
          )}
          {uiState === "completed_not_fetched" && (
            <p className="text-[13px] text-[#2A5B46] mt-1 font-medium">Report ready — click Fetch News to load articles.</p>
          )}
          {uiState === "error" && (
            <p className="text-[13px] text-[#A63446] mt-1">An error occurred. Try checking the status again.</p>
          )}
          {zeroArticles && (
            <p className="text-[12px] text-[#A63446] mt-1">
              Report returned 0 articles. The report may still be processing — try again in a moment.
            </p>
          )}
        </div>

        <div className="flex items-center gap-2 flex-shrink-0">
          <button
            disabled
            title="Report already in progress"
            className="px-4 py-2 rounded-md text-[12px] font-medium cursor-not-allowed"
            style={{ background: "#F4F5F6", color: "#9AA0A6", border: "1px solid #DCDFE2" }}
            data-testid="generate-btn-disabled"
          >
            Generate
          </button>

          {uiState === "in_progress" && (
            <button
              onClick={() => checkStatus(reportId)}
              className="px-4 py-2 rounded-md text-[12px] font-medium transition-colors hover:bg-[#F4F5F6] active:bg-[#DCDFE2]"
              style={{ border: "1px solid #3A3A3A", color: "#3A3A3A", background: "#FFFFFF" }}
              data-testid="check-status-btn"
            >
              Check Status
            </button>
          )}

          {uiState === "completed_not_fetched" && (
            <button
              onClick={handleFetchNews}
              disabled={fetching}
              className="flex items-center gap-1.5 px-4 py-2 rounded-md text-[13px] font-semibold text-white transition-all hover:opacity-90 active:opacity-75 disabled:opacity-60"
              style={{ background: "#2A5B46" }}
              data-testid="fetch-news-btn"
            >
              {fetching ? <><Loader2 size={12} className="animate-spin" /> Fetching...</> : "Fetch News →"}
            </button>
          )}
        </div>
      </div>

      {/* Manual report ID failsafe — always visible in State 3 */}
      {uiState === "completed_not_fetched" && (
        <div className="mt-4 pt-3" style={{ borderTop: "1px dashed #DCDFE2" }}>
          <p style={{ fontSize: "11px", color: "#9AA0A6", marginBottom: "6px" }}>
            Having trouble? Enter report ID manually:
          </p>
          <div className="flex items-center gap-2">
            <input
              type="text"
              value={manualIdInput}
              onChange={e => setManualIdInput(e.target.value.replace(/\D/g, ""))}
              placeholder={displayReportId ? String(displayReportId) : "e.g. 106447"}
              className="h-8 px-2.5 rounded-md text-[12px] focus:outline-none w-28"
              style={{ border: "1px solid #DCDFE2", background: "#FFFFFF" }}
              data-testid="manual-report-id-panel"
            />
            <button
              onClick={handleManualFetch}
              disabled={!manualIdInput.trim() || manualFetching}
              className="h-8 px-3 rounded-md text-[12px] font-medium text-white transition-all hover:opacity-90 disabled:opacity-50"
              style={{ background: "#3A3A3A" }}
              data-testid="manual-fetch-btn-panel"
            >
              {manualFetching ? <Loader2 size={11} className="animate-spin" /> : "Fetch →"}
            </button>
          </div>
        </div>
      )}

      <div className="mt-4 pt-3" style={{ borderTop: "1px solid #F0F2F4" }}>
        <div className="flex items-center justify-between">
          <button onClick={handleGoToMatrix} className="text-[12px] text-[#6B7280] hover:text-[#0A0A0A] transition-colors hover:underline" data-testid="go-to-matrix-btn">
            → Track progress in Portfolio Matrix
          </button>
          <button
            onClick={() => setResetConfirm(true)}
            className="text-[11px] transition-colors"
            style={{ color: "#9AA0A6" }}
            onMouseEnter={e => { e.currentTarget.style.color = "#6B7280"; }}
            onMouseLeave={e => { e.currentTarget.style.color = "#9AA0A6"; }}
            data-testid="reset-link-panel"
          >
            ↻ Reset & Regenerate
          </button>
        </div>
      </div>

      {resetConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center" style={{ background: "rgba(0,0,0,0.45)" }} onClick={() => setResetConfirm(false)}>
          <div className="bg-white rounded-md shadow-2xl p-6" style={{ maxWidth: "380px", width: "90%" }} onClick={e => e.stopPropagation()}>
            <h3 className="text-[15px] font-semibold text-[#0A0A0A] mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>
              Reset Coverage for {companyName}?
            </h3>
            <p className="text-[13px] text-[#6B7280] mb-5">This will delete existing coverage data and generate a fresh report. Continue?</p>
            <div className="flex gap-3 justify-end">
              <button onClick={() => setResetConfirm(false)} className="px-4 py-2 rounded-md text-[13px] font-medium text-[#6B7280] hover:bg-[#F4F5F6] transition-colors" style={{ border: "1px solid #DCDFE2" }} data-testid="reset-cancel-panel">Cancel</button>
              <button onClick={handleReset} disabled={resetting} className="px-4 py-2 rounded-md text-[13px] font-semibold text-white transition-colors hover:opacity-90 disabled:opacity-60" style={{ background: "#A63446" }} data-testid="reset-confirm-panel">
                {resetting ? "Resetting..." : "Yes, Reset & Regenerate"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Main Page ───────────────────────────────────────────────────────────────────
export default function CompanyNews() {
  const navigate     = useNavigate();
  const [searchParams] = useSearchParams();
  const companyParam   = searchParams.get("company");

  // Search panel collapsed state
  const [searchOpen, setSearchOpen] = useState(() => {
    try { const v = localStorage.getItem("wokelo_companynews_search_open"); return v === "true"; } catch { return false; }
  });
  const toggleSearch = () => setSearchOpen(prev => {
    const next = !prev;
    try { localStorage.setItem("wokelo_companynews_search_open", String(next)); } catch {}
    return next;
  });

  // Search bar state
  const [searchInput, setSearchInput] = useState("");
  const [generating,  setGenerating]  = useState(false);

  // Loaded company data (cached articles)
  const [cachedData,    setCachedData]    = useState(null);
  const [loadedCompany, setLoadedCompany] = useState(null);

  // In-progress state (when URL param company has a report running)
  const [companyPendingState, setCompanyPendingState] = useState(null); // { status, reportId, name }

  // Filters
  const [activeType,    setActiveType]    = useState("All");
  const [articleSearch, setArticleSearch] = useState("");
  const [currentPage,   setCurrentPage]   = useState(0);
  const ITEMS_PER_PAGE = 20;

  // ── Report-ID direct fetch state ──────────────────────────────────────────
  const [reportIdInput,    setReportIdInput]    = useState("");
  const [fetchingById,     setFetchingById]     = useState(false);
  // null | "checking" | "processing" | "no_data" | "error" | "completed"
  const [reportIdStatus,   setReportIdStatus]   = useState(null);
  const [reportIdBorderErr, setReportIdBorderErr] = useState(false);
  // { articles, companyName, reportId } once fetched
  const [reportIdFetchData, setReportIdFetchData] = useState(null);
  // null | "added" | "already_added"
  const [addedState,       setAddedState]        = useState(null);
  // Article viewer reset (State 4 on Company News)
  const [articleResetConfirm, setArticleResetConfirm] = useState(false);
  const [articleResetting,    setArticleResetting]    = useState(false);

  // Load company state from URL param
  useEffect(() => {
    setActiveType("All");
    setArticleSearch("");
    setCurrentPage(0);
    setCompanyPendingState(null);
    setCachedData(null);
    setLoadedCompany(null);
    // Clear Report ID fetch state so it doesn't bleed into URL-param company view
    setReportIdFetchData(null);
    setReportIdStatus(null);
    setAddedState(null);
    setReportIdInput("");

    if (!companyParam) return;

    const cached = getCompanyNews(companyParam);

    // Case 1: Cache exists with articles → render directly, skip all generation UI
    if (cached?.articles?.length > 0) {
      setLoadedCompany(companyParam);
      setCachedData(cached);
      return;
    }

    // Case 2: Cache exists but articles are empty → show error, not Fetch News
    if (cached && cached.articles !== undefined && cached.articles.length === 0) {
      setCompanyPendingState({ status: "empty_cache", reportId: cached.reportId, name: cached.companyName || companyParam });
      return;
    }

    const stored = getSearchedCompany(companyParam);
    if (!stored) {
      // No searched company entry — check if a report_id was stored (e.g., via Portfolio Matrix modal)
      // Auto-fetch articles so the user doesn't see a blank "No coverage" page
      const storedRptId = getStoredReportId(companyParam);
      if (storedRptId) {
        // Trigger an automatic fetch without requiring user action
        setCompanyPendingState({ status: "auto_fetching", reportId: storedRptId, name: companyParam });
        (async () => {
          try {
            const res = await fetch(`${API}/company-news/fetch`, {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ report_id: storedRptId, page: 1, page_size: 100 }),
            });
            if (!res.ok) throw new Error(`API error ${res.status}`);
            const data = await res.json();
            const articles = data?.data || [];
            if (articles.length > 0) {
              storeReportId(companyParam, storedRptId);
              setCompanyNewsCache(companyParam, articles, storedRptId, companyParam);
              addSearchedCompany(companyParam, companyParam, companyParam, storedRptId, "completed");
              setLoadedCompany(companyParam);
              setCachedData({ articles, reportId: storedRptId, companyName: companyParam });
              setCompanyPendingState(null);
            } else {
              setCompanyPendingState({ status: "empty_cache", reportId: storedRptId, name: companyParam });
            }
          } catch {
            setCompanyPendingState({ status: "completed_not_fetched", reportId: storedRptId, name: companyParam });
          }
        })();
      }
      return;
    }

    const sStatus = stored.status || "idle";
    if (["completed_not_fetched", "completed"].includes(sStatus)) {
      setCompanyPendingState({ status: "completed_not_fetched", reportId: stored.reportId, name: stored.name || companyParam });
    } else if (["queued", "processing", "starting", "delayed", "in_progress"].includes(sStatus)) {
      setCompanyPendingState({ status: "in_progress", reportId: stored.reportId, name: stored.name || companyParam });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyParam]);

  // Search input preview
  const inputPreview = useMemo(() => {
    const t = searchInput.trim();
    if (!t) return null;
    if (t.includes(".") && !t.includes(" ")) {
      const domain = t.replace(/^https?:\/\//, "").replace(/\/.*$/, "");
      return `Will search for: ${domain}`;
    }
    return `Will search for: ${t}`;
  }, [searchInput]);

  // Handle Generate
  const handleGenerate = async () => {
    const name = searchInput.trim();
    if (!name || generating) return;

    // Parse input: strip http/https and path
    const parsed = name.replace(/^https?:\/\//, "").replace(/\/.*$/, "").trim() || name;
    const permalink = derivePermalink(parsed);

    // State 4: cached → navigate to viewer
    const cached = getCompanyNews(permalink);
    if (cached?.articles?.length > 0) {
      navigate(`/company-news?company=${encodeURIComponent(permalink)}`);
      return;
    }

    const stored = getSearchedCompany(permalink);

    // State 3: completed not fetched → navigate to company page (status panel will show)
    if (stored?.reportId && ["completed_not_fetched", "completed"].includes(stored.status)) {
      navigate(`/company-news?company=${encodeURIComponent(permalink)}`);
      return;
    }

    // State 2: in progress (has reportId) OR API call in flight (status "starting", no reportId yet)
    if (stored?.reportId || ["starting", "queued", "processing", "in_progress", "delayed"].includes(stored?.status)) {
      navigate("/portfolio-matrix");
      return;
    }

    // State 1: no report — call /start/ and redirect to Portfolio Matrix
    setGenerating(true);
    try {
      if (!stored) addSearchedCompany(name, permalink, permalink, null, "starting");
      // Store confirmed website so Portfolio Matrix can reuse it
      setConfirmedWebsite(permalink, parsed);

      const payload = parsed.includes(".") ? { website: parsed } : { permalink: parsed };
      const res = await fetch(`${API}/company-news/start`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!res.ok) { setGenerating(false); return; }

      const data     = await res.json();
      const newId    = data.report_id;
      updateSearchedCompanyStatus(permalink, "queued", newId);
      setSearchInput("");
      navigate("/portfolio-matrix");
    } catch { setGenerating(false); }
  };

  // Handle Refresh (from article viewer)
  const handleRefresh = () => {
    if (!loadedCompany) return;
    clearCompanyNewsCacheEntry(loadedCompany);
    clearSearchedCompanyReport(loadedCompany);
    navigate("/portfolio-matrix");
  };

  // Handle Reset & Regenerate from article viewer (State 4)
  const handleArticleReset = async () => {
    if (!loadedCompany) return;
    setArticleResetting(true);
    const cn = activeData?.companyName || loadedCompany;
    clearCompanyNewsCacheEntry(loadedCompany);
    addSearchedCompany(cn, loadedCompany, loadedCompany, null, "starting");
    const cw = getConfirmedWebsite(loadedCompany) || cn;
    const payload = cw.includes(".") ? { website: cw } : { permalink: cw };
    try {
      const res = await fetch(`${API}/company-news/start`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error("Start failed");
      const data = await res.json();
      updateSearchedCompanyStatus(loadedCompany, "queued", data.report_id);
      setArticleResetConfirm(false);
      navigate("/portfolio-matrix");
    } catch {
      setArticleResetting(false);
      setArticleResetConfirm(false);
    }
  };

  // ── Fetch by Report ID helpers ────────────────────────────────────────────
  // Derive permalink per spec: lowercase, spaces→hyphens, strip non-alphanum
  const deriveLocalPermalink = useCallback((name) =>
    name.toLowerCase().trim().replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, ""), []);

  // Step 1: check status; Step 2a: fetch articles if completed
  const handleCheckStatus = useCallback(async (id) => {
    setFetchingById(true);
    setReportIdStatus("checking");
    setReportIdBorderErr(false);
    try {
      const statusRes = await fetch(`${API}/company-news/status/${id}`);
      if (!statusRes.ok) throw new Error("Status check failed");
      const statusData = await statusRes.json();
      const s = (statusData.status || "").toLowerCase();

      if (s === "completed" || s === "exported") {
        // Step 2a: fetch articles
        const fetchRes = await fetch(`${API}/company-news/fetch`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ report_id: parseInt(id, 10), page: 1, page_size: 100 }),
        });
        if (!fetchRes.ok) throw new Error("Fetch failed");
        const fd = await fetchRes.json();
        const articles = fd.data || [];
        const cn = articles.find(a => a.company_name)?.company_name || `Report #${id}`;
        setReportIdFetchData({ articles, companyName: cn, reportId: parseInt(id, 10), fetchedAt: new Date().toISOString() });
        setReportIdStatus("completed");
        // Check if already saved to localStorage
        const pl = deriveLocalPermalink(cn);
        setAddedState(getSearchedCompany(pl) ? "already_added" : null);
      } else if (["processing", "queued", "delayed", "in_progress"].includes(s)) {
        // Step 2b
        setReportIdStatus("processing");
      } else {
        // Step 2c: no data found or unknown error
        setReportIdStatus("no_data");
        setReportIdBorderErr(true);
      }
    } catch {
      setReportIdStatus("error");
      setReportIdBorderErr(true);
    } finally {
      setFetchingById(false);
    }
  }, [deriveLocalPermalink]);

  const handleFetchById = () => {
    const id = parseInt(reportIdInput.trim(), 10);
    if (!id || isNaN(id) || fetchingById) return;
    handleCheckStatus(id);
  };

  const handleCheckAgain = () => {
    const id = parseInt(reportIdInput.trim(), 10);
    if (!id || isNaN(id)) return;
    handleCheckStatus(id);
  };

  const handleAddToSearched = () => {
    if (!reportIdFetchData || addedState) return;
    const { articles, companyName: cn, reportId } = reportIdFetchData;
    const permalink = deriveLocalPermalink(cn);
    console.log(`[AddToSearched] Storing ${articles.length} articles under key: wokelo_company_news_${permalink}`);
    addSearchedCompany(cn, permalink, "", parseInt(reportId, 10), "completed");
    setCompanyNewsCache(permalink, articles, parseInt(reportId, 10), cn);
    // Verify write succeeded
    const verify = getCompanyNews(permalink);
    console.log(`[AddToSearched] Cache verify: ${verify?.articles?.length ?? 0} articles stored for key: ${permalink}`);
    setAddedState("added");
  };

  // activeData: URL-param cached company takes priority over report-ID fetch
  const activeData = (loadedCompany && cachedData) ? cachedData : reportIdFetchData;
  const isFromReportIdFetch = !!(reportIdFetchData && !(loadedCompany && cachedData));

  // Derived article type counts
  const typeCounts = useMemo(() => {
    if (!activeData?.articles) return {};
    return activeData.articles.reduce((acc, a) => {
      const t = a.type || "Other";
      acc[t] = (acc[t] || 0) + 1;
      return acc;
    }, {});
  }, [activeData]);

  const types = ["All", ...Object.keys(typeCounts).sort((a, b) => (typeCounts[b] || 0) - (typeCounts[a] || 0))];

  const filteredArticles = useMemo(() => {
    if (!activeData?.articles) return [];
    return activeData.articles.filter(a => {
      if (activeType !== "All" && (a.type || "Other") !== activeType) return false;
      if (articleSearch) {
        const q = articleSearch.toLowerCase();
        return (a.title || "").toLowerCase().includes(q) || (a.ai_summary || "").toLowerCase().includes(q);
      }
      return true;
    });
  }, [activeData, activeType, articleSearch]);

  const totalPages   = Math.ceil(filteredArticles.length / ITEMS_PER_PAGE);
  const pagedArticles = filteredArticles.slice(currentPage * ITEMS_PER_PAGE, (currentPage + 1) * ITEMS_PER_PAGE);
  const companyName  = activeData?.companyName || loadedCompany?.replace(/-/g, " ").replace(/\b\w/g, c => c.toUpperCase()) || "";

  return (
    <div style={{ background: "#FFFFFF", minHeight: "100%" }}>
      <div style={{ padding: "24px 32px", maxWidth: "1100px", margin: "0 auto" }}>

        {/* Page Header */}
        <div className="mb-6">
          <h1 className="font-bold text-[#0A0A0A] mb-1" style={{ fontFamily: "'Playfair Display', serif", fontSize: "28px" }}>
            Company News Coverage
          </h1>
          <p className="text-[13px] text-[#6B7280]">
            Search for any company to generate AI-powered news intelligence
          </p>
        </div>

        {/* ── Search Section — collapsible, hidden by default ── */}
        <div
          style={{
            overflow: "hidden",
            maxHeight: searchOpen ? "500px" : "0",
            transition: "max-height 200ms ease",
          }}
        >
          <div className="bg-white rounded-md p-5 mb-4" style={{ boxShadow: "0 1px 4px rgba(0,0,0,0.08)" }}>
            <div className="flex items-center justify-between mb-3">
              <label className="text-[11px] font-semibold text-[#9AA0A6] uppercase tracking-wide" style={{ letterSpacing: "0.08em" }}>
                Search Company News Intelligence
              </label>
              <button
                onClick={toggleSearch}
                className="text-[11px] text-[#9AA0A6] hover:text-[#4A4A4A] transition-colors"
                data-testid="search-panel-hide"
              >
                − Hide
              </button>
            </div>

          <div className="flex gap-6">
            {/* ── Left column: company name / Generate ── */}
            <div className="flex-1 min-w-0">
              <div className="flex gap-3">
                <div className="relative flex-1">
                  <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9AA0A6]" />
                  <input
                    type="text"
                    value={searchInput}
                    onChange={e => setSearchInput(e.target.value)}
                    onKeyDown={e => e.key === "Enter" && handleGenerate()}
                    placeholder="Enter company name, website, or Crunchbase permalink..."
                    data-testid="company-search-input"
                    className="w-full h-11 pl-10 pr-4 rounded-md text-[14px] placeholder:text-[#9AA0A6] focus:outline-none transition-colors"
                    style={{ border: "1px solid #DCDFE2", background: "#FFFFFF" }}
                  />
                </div>
                <button
                  onClick={handleGenerate}
                  disabled={!searchInput.trim() || generating}
                  className="px-6 h-11 rounded-md text-[13px] font-semibold text-white transition-all disabled:opacity-50 disabled:cursor-not-allowed hover:opacity-90 active:opacity-75 flex-shrink-0"
                  style={{ background: "#3A3A3A" }}
                  data-testid="generate-btn"
                >
                  {generating ? <Loader2 size={14} className="animate-spin" /> : "Generate"}
                </button>
              </div>
              <p className="text-[11px] text-[#9AA0A6] mt-2">Examples: Apple, apple.com, uber-china</p>
              {inputPreview && (
                <p className="text-[11px] text-[#9AA0A6] mt-1 italic" data-testid="search-preview">{inputPreview}</p>
              )}
            </div>

            {/* ── Right column: Report ID / Fetch Report ── */}
            <div className="flex-1 min-w-0">
              <div className="flex gap-3">
                <div className="relative flex-1">
                  <input
                    type="text"
                    value={reportIdInput}
                    onChange={e => { setReportIdInput(e.target.value); setReportIdBorderErr(false); setReportIdStatus(null); }}
                    onKeyDown={e => e.key === "Enter" && handleFetchById()}
                    placeholder="Enter existing Report ID..."
                    data-testid="report-id-input"
                    className="w-full h-11 px-4 rounded-md text-[14px] placeholder:text-[#9AA0A6] focus:outline-none transition-colors"
                    style={{
                      border: `1px solid ${reportIdBorderErr ? "#A63446" : "#E8EBED"}`,
                      background: "#FFFFFF",
                    }}
                  />
                </div>
                <button
                  onClick={handleFetchById}
                  disabled={!reportIdInput.trim() || fetchingById}
                  className="px-5 h-11 rounded-md text-[13px] font-semibold transition-all disabled:opacity-50 disabled:cursor-not-allowed hover:bg-[#F4F5F6] active:bg-[#DCDFE2] flex-shrink-0 flex items-center gap-1.5"
                  style={{ border: "1px solid #9AA0A6", color: "#3A3A3A", background: "#FFFFFF" }}
                  data-testid="fetch-report-btn"
                >
                  {fetchingById
                    ? <><Loader2 size={13} className="animate-spin" /> Fetching...</>
                    : "Fetch Report →"
                  }
                </button>
              </div>
              <p className="text-[11px] text-[#9AA0A6] mt-2">
                Already have a report ID? Fetch its articles directly.
              </p>

              {/* Status feedback (Step 2b / 2c) */}
              {reportIdStatus === "processing" && (
                <div className="mt-2 flex items-center gap-3">
                  <div className="flex items-center gap-1.5">
                    <div className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: "#F59E0B" }} />
                    <span className="text-[12px] text-[#6B7280]">This report is still processing. Check back shortly.</span>
                  </div>
                  <button
                    onClick={handleCheckAgain}
                    className="text-[12px] font-medium text-[#3A3A3A] hover:underline transition-colors"
                    data-testid="check-again-btn"
                  >
                    Check Again
                  </button>
                </div>
              )}
              {(reportIdStatus === "no_data" || reportIdStatus === "error") && (
                <p className="text-[12px] mt-2" style={{ color: "#A63446" }} data-testid="report-id-error-msg">
                  No report found for this ID. Please verify and try again.
                </p>
              )}
            </div>
          </div>
          </div> {/* end white card */}
        </div> {/* end collapsible search wrapper */}

        {/* Search toggle trigger */}
        <div className="mb-4">
          {!searchOpen && (
            <button
              onClick={toggleSearch}
              className="text-xs text-[#9AA0A6] hover:text-[#4A4A4A] transition-colors flex items-center gap-1"
              data-testid="search-panel-toggle"
            >
              ⌄ New search
            </button>
          )}
        </div>

        {/* Company Status Panel (States 2 & 3) — only when no articles in cache */}
        {companyPendingState && !activeData && (
          <div className="mb-6">
            {companyPendingState.status === "auto_fetching" ? (
              /* Auto-fetching via stored report_id */
              <div className="bg-white rounded-md p-6" style={{ boxShadow: "0 1px 4px rgba(0,0,0,0.08)", border: "1px solid #E5E7EB" }}>
                <div className="flex items-center gap-3">
                  <Loader2 size={18} className="animate-spin text-[#9AA0A6]" />
                  <p className="text-[13px] text-[#6B7280]">Loading news coverage for <strong>{companyPendingState.name}</strong>…</p>
                </div>
              </div>
            ) : companyPendingState.status === "empty_cache" ? (
              /* Empty cache error — cache exists but returned 0 articles */
              <div className="bg-white rounded-md p-6" style={{ boxShadow: "0 1px 4px rgba(0,0,0,0.08)", border: "1px solid #FECACA" }}>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: "#FEF2F2" }}>
                    <span style={{ fontSize: "18px" }}>⚠</span>
                  </div>
                  <div>
                    <p className="text-[14px] font-semibold text-[#0A0A0A] mb-1">No articles found for {companyPendingState.name}</p>
                    <p className="text-[13px] text-[#6B7280]">
                      The report completed but returned no articles. This may happen if the company identifier was incorrect.
                    </p>
                    <button
                      onClick={() => { setCompanyPendingState(null); }}
                      className="mt-3 text-[12px] text-[#3A3A3A] hover:underline underline-offset-2 transition-colors"
                    >
                      Try searching for this company again →
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <CompanyStatusPanel
                permalink={companyParam}
                companyName={companyPendingState.name}
                reportId={companyPendingState.reportId}
                initialStatus={companyPendingState.status}
                onFetchSuccess={(plink, fresh) => {
                  setLoadedCompany(plink);
                  setCachedData(fresh);
                  setCompanyPendingState(null);
                }}
              />
            )}
          </div>
        )}

        {/* Article Viewer — shows for URL-param company OR report-ID fetch */}
        {activeData ? (
          <div className="bg-white rounded-md overflow-hidden" style={{ boxShadow: "0 1px 4px rgba(0,0,0,0.08)" }}>
            {/* Header */}
            <div className="px-6 py-5" style={{ borderBottom: "1px solid #DCDFE2" }}>
              <div className="flex items-start justify-between gap-4">
                <div>
                  <h2 className="font-bold text-[#0A0A0A] mb-1" style={{ fontFamily: "'Playfair Display', serif", fontSize: "32px" }} data-testid="company-name-heading">
                    {companyName}
                  </h2>
                  <p className="text-[12px] text-[#9AA0A6]">
                    Last fetched: {getRelativeTime(activeData.fetchedAt)}
                    {activeData.reportId && (
                      <span className="ml-2 font-mono text-[#9AA0A6]">· Report #{activeData.reportId}</span>
                    )}
                  </p>
                  {/* "Add to Companies You Searched" — only for report-ID fetch */}
                  {isFromReportIdFetch && (
                    <div className="mt-3">
                      {addedState === "added" ? (
                        <button
                          disabled
                          className="px-3 py-1.5 rounded-md text-[11px] font-semibold cursor-not-allowed"
                          style={{ background: "#2A5B46", color: "#FFFFFF", border: "1px solid #2A5B46" }}
                          data-testid="add-to-searched-btn-done"
                        >
                          ✓ Added to Companies You Searched
                        </button>
                      ) : addedState === "already_added" ? (
                        <button
                          disabled
                          className="px-3 py-1.5 rounded-md text-[11px] font-semibold cursor-not-allowed"
                          style={{ background: "transparent", color: "#2A5B46", border: "1px solid #2A5B46", opacity: 0.7 }}
                          data-testid="add-to-searched-btn-exists"
                        >
                          ✓ Already in Companies You Searched
                        </button>
                      ) : (
                        <button
                          onClick={handleAddToSearched}
                          className="px-3 py-1.5 rounded-md text-[11px] font-semibold transition-all hover:bg-[#2A5B4610] active:opacity-75"
                          style={{ color: "#2A5B46", border: "1px solid #2A5B46", background: "transparent" }}
                          data-testid="add-to-searched-btn"
                        >
                          + Add to Companies You Searched
                        </button>
                      )}
                    </div>
                  )}
                </div>
                {!isFromReportIdFetch && (
                  <div className="flex flex-col items-end gap-1">
                    <button
                      onClick={handleRefresh}
                      className="flex items-center gap-1.5 px-3 py-2 rounded-md text-[12px] font-medium text-[#6B7280] hover:text-[#97C8A6] transition-colors"
                      style={{ border: "1px solid #DCDFE2", background: "#FFFFFF" }}
                      data-testid="refresh-btn"
                    >
                      ↻ Refresh
                    </button>
                    <button
                      onClick={() => setArticleResetConfirm(true)}
                      className="text-[11px] transition-colors"
                      style={{ color: "#9AA0A6" }}
                      onMouseEnter={e => { e.currentTarget.style.color = "#6B7280"; }}
                      onMouseLeave={e => { e.currentTarget.style.color = "#9AA0A6"; }}
                      data-testid="reset-link-article"
                    >
                      ↻ Reset & Regenerate
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Article reset confirmation modal */}
            {articleResetConfirm && (
              <div
                className="fixed inset-0 z-50 flex items-center justify-center"
                style={{ background: "rgba(0,0,0,0.45)" }}
                onClick={() => setArticleResetConfirm(false)}
              >
                <div
                  className="bg-white rounded-md shadow-2xl p-6"
                  style={{ maxWidth: "380px", width: "90%" }}
                  onClick={e => e.stopPropagation()}
                >
                  <h3 className="text-[15px] font-semibold text-[#0A0A0A] mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>
                    Reset Coverage for {companyName}?
                  </h3>
                  <p className="text-[13px] text-[#6B7280] mb-5">
                    This will delete existing coverage data and generate a fresh report. Continue?
                  </p>
                  <div className="flex gap-3 justify-end">
                    <button
                      onClick={() => setArticleResetConfirm(false)}
                      className="px-4 py-2 rounded-md text-[13px] font-medium text-[#6B7280] hover:bg-[#F4F5F6] transition-colors"
                      style={{ border: "1px solid #DCDFE2" }}
                      data-testid="article-reset-cancel"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleArticleReset}
                      disabled={articleResetting}
                      className="px-4 py-2 rounded-md text-[13px] font-semibold text-white transition-colors hover:opacity-90 disabled:opacity-60"
                      style={{ background: "#A63446" }}
                      data-testid="article-reset-confirm"
                    >
                      {articleResetting ? "Resetting..." : "Yes, Reset & Regenerate"}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Category Pills + Article Search */}
            <div className="px-6 py-4" style={{ borderBottom: "1px solid #F0F2F4", background: "#FAFBFC" }}>
              <div className="flex gap-2 flex-wrap mb-3">
                {types.map(type => {
                  const c = TYPE_COLORS[type] || DEFAULT_TYPE_COLOR;
                  const isActive = activeType === type;
                  const count    = type === "All" ? activeData.articles.length : typeCounts[type];
                  return (
                    <button
                      key={type}
                      onClick={() => { setActiveType(type); setCurrentPage(0); }}
                      className="px-3 py-1.5 rounded-md text-[12px] font-medium transition-all hover:opacity-80 active:opacity-60"
                      style={{
                        background: isActive ? (type === "All" ? "#0A0A0A" : c.bg) : "#FFFFFF",
                        color: isActive ? (type === "All" ? "#FFFFFF" : c.text) : "#6B7280",
                        border: `1px solid ${isActive ? (type === "All" ? "#0A0A0A" : c.border) : "#DCDFE2"}`,
                      }}
                      data-testid={`filter-pill-${type}`}
                    >
                      {type} <span className="ml-1 opacity-70">{count}</span>
                    </button>
                  );
                })}
              </div>

              <div className="relative" style={{ maxWidth: "320px" }}>
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9AA0A6]" />
                <input
                  type="text"
                  value={articleSearch}
                  onChange={e => { setArticleSearch(e.target.value); setCurrentPage(0); }}
                  placeholder="Search articles..."
                  data-testid="article-search-input"
                  className="w-full h-9 pl-9 pr-3 rounded-md text-[12px] placeholder:text-[#9AA0A6] focus:outline-none"
                  style={{ border: "1px solid #DCDFE2", background: "#FFFFFF" }}
                />
              </div>
            </div>

            {/* Article List */}
            <div className="px-6">
              {pagedArticles.length === 0 ? (
                <div className="py-12 text-center">
                  <p className="text-[13px] text-[#9AA0A6] italic">
                    {articleSearch || activeType !== "All" ? "No articles match your filters." : "No articles found."}
                  </p>
                </div>
              ) : (
                <div data-testid="article-list">
                  {pagedArticles.map((article, idx) => <ArticleCard key={idx} article={article} />)}
                </div>
              )}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between px-6 py-4" style={{ borderTop: "1px solid #F0F2F4", background: "#FAFBFC" }}>
                <button
                  onClick={() => setCurrentPage(p => Math.max(0, p - 1))}
                  disabled={currentPage === 0}
                  className="flex items-center gap-1 px-3 py-1.5 rounded-md text-[12px] font-medium disabled:opacity-40 transition-colors hover:bg-[#F4F5F6] active:bg-[#DCDFE2]"
                  style={{ border: "1px solid #DCDFE2", background: "#FFFFFF", color: "#3A3A3A" }}
                  data-testid="prev-page-btn"
                >
                  <ChevronLeft size={12} /> Previous
                </button>
                <span className="text-[12px] text-[#6B7280]">
                  Page {currentPage + 1} of {totalPages} · {filteredArticles.length} articles
                </span>
                <button
                  onClick={() => setCurrentPage(p => Math.min(totalPages - 1, p + 1))}
                  disabled={currentPage >= totalPages - 1}
                  className="flex items-center gap-1 px-3 py-1.5 rounded-md text-[12px] font-medium disabled:opacity-40 transition-colors hover:bg-[#F4F5F6] active:bg-[#DCDFE2]"
                  style={{ border: "1px solid #DCDFE2", background: "#FFFFFF", color: "#3A3A3A" }}
                  data-testid="next-page-btn"
                >
                  Next <ChevronRight size={12} />
                </button>
              </div>
            )}
          </div>
        ) : !companyPendingState ? (
          /* Empty/welcome state */
          <div className="bg-white rounded-md p-12 text-center" style={{ boxShadow: "0 1px 4px rgba(0,0,0,0.08)" }}>
            <Search size={36} className="text-[#DCDFE2] mx-auto mb-4" />
            <p className="text-[14px] text-[#9AA0A6] italic">
              {companyParam
                ? `No coverage data for "${companyParam}". Generate a report from Portfolio Matrix.`
                : "Search for a company above to generate AI-powered news coverage."}
            </p>
          </div>
        ) : null}

      </div>
    </div>
  );
}
